/**
 * §13.1.8 Victory. Panel after the 1.2 s slow-collapse: result, run time, build
 * icons, Continue to Summary. The rising-mote confetti analogue is a CSS particle
 * strip that is suppressed under reduced flashes (photosafety, §11.8).
 */

import { weaponById } from '../../core/content';
import { CaptainCard } from '../captain-card';
import type { Captain } from '../../captain/seed';
import { button, div, icon, panel, panelBody, setText } from '../dom';
import { formatTime } from '../format';
import type { World } from '../../sim/world';
import { Screen, type ActionSink } from './base';

/*
 * 14 motes put a combined 303 px2 on screen — **0.092% of the viewport**, about five crossing
 * the frame per second. That is not a confetti-analogue; it is the occasional speck.
 *
 * §13.1.8 asks for the effect to be "photosafety-checked", and measuring it showed how much
 * headroom that constraint actually leaves: WCAG 2.3.1 is about flashing over more than 25% of
 * the viewport, and this is three hundred times under. 48 brings it to roughly 0.3% and about
 * eighteen crossings a second — a stream you can see, still nowhere near the limit that made
 * the spec cautious.
 */
const MOTE_COUNT = 48;

export class VictoryScreen extends Screen {
  private timeText: HTMLElement;
  private buildRow: HTMLElement;
  private motes: HTMLElement;
  /**
   * The captain who came back.
   *
   * Death got a card — a 3× portrait, the name, the last thing they said — because §13.1.9
   * bet the whole captain feature on a playtester being able to name them an hour later.
   * Victory got four chips and a button. So the one outcome the player is actually chasing
   * was the one that never introduced the person who achieved it, and the game celebrated
   * dying more thoroughly than surviving. Same card, same face, opposite ending.
   */
  private readonly captainCard = new CaptainCard();

  constructor(emit: ActionSink) {
    super('victory', emit, 'is-modal');

    /*
     * §13.1.8 asks for a "confetti-analogue: rising mote stream". What it drew was a grid.
     *
     * Fourteen motes at exactly `(i + 0.5) / 14` of the width, every one 4x4, every one
     * rising for the same 2600 ms: evenly spaced columns of identical dots moving in step.
     * Regular enough to read as ruling rather than as anything drifting up out of a trench.
     *
     * Three properties now vary per mote, and none of them by chance — a captured frame has
     * to be reproducible, so each is a fixed function of the index. The multipliers are
     * coprime with the count, which is the whole trick: 5, 7 and 11 against 14 means the size
     * cycle, the speed cycle and the offset cycle do not come back into phase across the
     * stream, so no two neighbours share a signature and the pattern has no visible period.
     */
    this.motes = div('mote-stream');
    for (let i = 0; i < MOTE_COUNT; i++) {
      const m = div('mote');
      // Jitter off the even grid by up to a third of a column, alternating side.
      const lane = (i + 0.5) * (100 / MOTE_COUNT);
      const drift = (((i * 5) % 7) / 7 - 0.5) * (100 / MOTE_COUNT) * 0.66;
      m.style.left = (lane + drift).toFixed(2) + '%';
      const size = 3 + ((i * 7) % 4);
      m.style.inlineSize = size + 'px';
      m.style.blockSize = size + 'px';
      // Smaller motes rise slower, which is what reads as depth rather than as noise.
      m.style.animationDuration = 2200 + ((i * 11) % 5) * 300 + 'ms';
      /*
       * NEGATIVE delay, spread across the mote's own rise.
       *
       * The delays used to be `(i * 90) % 1200` — positive, and spanning 1.2 s against a rise
       * of 2.2-3.4 s. So every mote started below the screen and the pattern repeated every
       * thirteen: the stream arrived in waves with visible gaps between them, which a
       * four-frame capture caught as two empty frames out of four.
       *
       * A negative delay starts an animation already part-way through. Spreading it evenly
       * over each mote's own duration means the field is full at the instant the panel opens
       * and stays even, with no first-frame ramp and no pulse.
       */
      const dur = 2200 + ((i * 11) % 5) * 300;
      m.style.animationDuration = dur + 'ms, ' + (3000 + ((i * 5) % 4) * 400) + 'ms';
      m.style.animationDelay = '-' + Math.round(dur * (i / MOTE_COUNT)) + 'ms, -' + ((i * 7) % 9) * 350 + 'ms';
      /*
       * The graft, off the variant that lost: a sideways drift, "subtle and slow so it
       * suggests a gentle current" rather than the wobble both judge runs read in the
       * original. That one swung 6-18 px on a 1900 ms period; this swings 3-8 px over
       * 3000-4200 ms, and the period is deliberately unrelated to the rise so no mote
       * traces the same arc twice on its way up.
       */
      m.style.setProperty('--sway', (3 + ((i * 5) % 3) * 2.5) + 'px');
      this.motes.appendChild(m);
    }
    this.root.appendChild(this.motes);

    const p = panel('victory-panel');
    const body = panelBody(p);
    body.appendChild(div('screen-title accent', 'DESCENT SURVIVED'));
    body.appendChild(this.captainCard.root);
    /*
     * The headline says what it is. It was a bare "10:28" in 20 px gold, directly under a
     * line that already read "Dry · surfaced at 10:28" — the same value twice, and the larger
     * of the two unlabelled, so it had to be inferred from the colon.
     */
    const timeWrap = div('victory-stat');
    this.timeText = div('victory-time', '00:00');
    timeWrap.appendChild(this.timeText);
    // Value first, label under, and `.stat-label` rather than a new class: that is the shape
    // every stat on the summary grid already uses (`stat-value` then `stat-label`), and a
    // result screen inventing its own order for the same idea is how two screens drift.
    timeWrap.appendChild(div('stat-label', 'Run time'));
    body.appendChild(timeWrap);
    this.buildRow = div('victory-build');
    body.appendChild(this.buildRow);

    /*
     * The button lives in a `.victory-menu`, which is what makes AMD-57's pin reach it.
     *
     * `.victory-panel .victory-menu > .primary` has been in `ui.css` since AMD-57 gave the
     * result screens a sticky, opaque primary — and this screen never built a `.victory-menu`,
     * so both rules naming it matched nothing. Measured on a 844x390 landscape phone: the
     * panel overflows by 142 px and Continue sits at y 446..490 of a 390 px viewport — **off
     * screen at rest**, on the only control the screen has. At the 1.5 text scale it is 252 px
     * of overflow and y 555.
     *
     * Defeat and summary got the fix; victory was written without the wrapper the fix selects
     * on, so it was left behind silently.
     */
    const menu = div('victory-menu');
    const cont = button('Continue to Summary', 'primary', 'arrow-right');
    cont.addEventListener('click', () => this.emit({ type: 'goto', screen: 'summary' }));
    menu.appendChild(cont);
    body.appendChild(menu);
    this.root.appendChild(p);

    this.stops = [cont];
  }

  /** The card is fed by the UI, which owns the run's captain. */
  presentCaptain(c: Captain, runTimeS: number): void {
    this.captainCard.present(c, null, runTimeS, 'survived');
  }

  present(w: World): void {
    setText(this.timeText, formatTime(w.tick / 60));
    while (this.buildRow.firstChild) this.buildRow.removeChild(this.buildRow.firstChild);
    const p = w.player;
    for (let i = 0; i < p.weapon_count; i++) {
      const slot = p.weapons[i];
      if (!slot.def_id) continue;
      const chip = div('build-chip' + (slot.evolved ? ' is-evolved' : ''));
      chip.appendChild(icon(slot.evolved ? 'crown' : 'bolt'));
      const def = weaponById.get(slot.def_id);
      chip.appendChild(div('chip-label', (def ? def.display_name : slot.def_id) + ' ' + slot.level));
      this.buildRow.appendChild(chip);
    }
  }

  setReducedFlashes(on: boolean): void {
    this.motes.hidden = on;
  }

  override onBack(): boolean {
    this.emit({ type: 'goto', screen: 'summary' });
    return true;
  }
}
